import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, BUDGETS, CONTACT_METHODS, SITE } from "@/data/site";
import { SentNotice } from "@/components/contact/SentNotice";
import { scrollToId } from "@/lib/lenis";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const empty = { name: "", email: "", company: "", phone: "", service: "", budget: "", description: "", method: "Email", website: "" };

const validate = (v) => {
  const e = {};
  if (v.name.trim().length < 2) e.name = "Please enter your name.";
  if (!EMAIL_RE.test(v.email.trim())) e.email = "Please enter a valid email address.";
  if (!v.service) e.service = "Please choose the service you need.";
  if (v.description.trim().length < 20) e.description = "Please describe your project in at least 20 characters.";
  if (v.description.length > 1500) e.description = "Please keep the description under 1,500 characters.";
  if (v.method === "Phone call" && !/^\+?[\d\s-]{8,16}$/.test(v.phone.trim())) e.phone = "Please enter a phone number we can call.";
  return e;
};

const buildMessage = (v) => [
  `Name: ${v.name.trim()}`,
  `Email: ${v.email.trim()}`,
  v.company.trim() ? `Company: ${v.company.trim()}` : null,
  v.phone.trim() ? `Phone: ${v.phone.trim()}` : null,
  `Service: ${v.service}`,
  v.budget ? `Estimated budget: ${v.budget}` : null,
  `Preferred contact: ${v.method}`,
  "",
  "Project description:",
  v.description.trim(),
].filter((l) => l !== null).join("\n");

const inputCls = (err) => `mt-2 block w-full rounded-md border bg-white px-4 py-3 text-[15px] text-slate-950 placeholder:text-slate-400 transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-4 ${err ? "border-red-500 focus:ring-red-100" : "border-slate-300 focus:border-blue-600 focus:ring-blue-100"}`;

const Field = ({ id, label, optional, error, children }) => (
  <div>
    <label htmlFor={id} className="text-[14px] font-semibold text-slate-900">{label}{optional ? <span className="ml-1 font-normal text-slate-500">(optional)</span> : <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>}</label>
    {children}
    {error && <p id={`${id}-error`} className="mt-1.5 text-[13px] font-medium text-red-700" data-testid={`contact-error-${id}`}>{error}</p>}
  </div>
);

export const ContactForm = ({ initialService }) => {
  const [v, setV] = useState({ ...empty, service: initialService });
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  const set = (k) => (e) => { setV((p) => ({ ...p, [k]: e.target.value })); if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined })); };
  const aria = (k) => ({ "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `${k}-error` : undefined });

  const submit = (e) => {
    e.preventDefault();
    if (v.website) return;
    const errs = validate(v);
    setErrors(errs);
    if (Object.keys(errs).length) {
      document.getElementById(Object.keys(errs)[0])?.focus();
      return;
    }
    const text = buildMessage(v);
    const subject = `Project enquiry: ${v.service} — ${v.name.trim()}`;
    const mailto = `mailto:${SITE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
    const wa = `${SITE.whatsapp}?text=${encodeURIComponent(`Hello Quantivo,\n\n${text}`)}`;
    const channel = v.method === "WhatsApp" ? "whatsapp" : "email";
    if (channel === "whatsapp") window.open(wa, "_blank", "noopener,noreferrer");
    else window.location.href = mailto;
    setResult({ text, mailto, wa, channel });
    setTimeout(() => scrollToId("enquiry"), 50);
  };

  if (result) return <SentNotice result={result} onReset={() => setResult(null)} />;

  return (
    <form noValidate onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-7 shadow-[0_30px_80px_-50px_rgba(15,23,42,0.35)] sm:p-10" data-testid="contact-form" aria-describedby="form-note">
      <h2 className="text-2xl font-extrabold tracking-tight text-slate-950">Tell us about your project</h2>
      <p id="form-note" className="mt-2 text-[14px] leading-relaxed text-slate-600">Submitting opens your email app (or WhatsApp) with your enquiry pre-filled, so you stay in control of what is sent.</p>
      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <Field id="name" label="Name" error={errors.name}><input id="name" autoComplete="name" value={v.name} onChange={set("name")} className={inputCls(errors.name)} {...aria("name")} data-testid="contact-form-name" /></Field>
        <Field id="email" label="Business email" error={errors.email}><input id="email" type="email" autoComplete="email" value={v.email} onChange={set("email")} className={inputCls(errors.email)} {...aria("email")} data-testid="contact-form-email" /></Field>
        <Field id="company" label="Company name" optional><input id="company" autoComplete="organization" value={v.company} onChange={set("company")} className={inputCls()} data-testid="contact-form-company" /></Field>
        <Field id="service" label="Service required" error={errors.service}>
          <select id="service" value={v.service} onChange={set("service")} className={inputCls(errors.service)} {...aria("service")} data-testid="contact-form-service">
            <option value="">Select a service</option>
            {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
            <option value="Other / Not sure">Other / Not sure</option>
          </select>
        </Field>
        <Field id="budget" label="Estimated budget" optional>
          <select id="budget" value={v.budget} onChange={set("budget")} className={inputCls()} data-testid="contact-form-budget">
            <option value="">Select a range</option>
            {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </Field>
        <Field id="phone" label="Phone number" optional={v.method !== "Phone call"} error={errors.phone}><input id="phone" type="tel" autoComplete="tel" value={v.phone} onChange={set("phone")} placeholder="+91 …" className={inputCls(errors.phone)} {...aria("phone")} data-testid="contact-form-phone" /></Field>
        <div className="sm:col-span-2">
          <Field id="description" label="Project description" error={errors.description}>
            <textarea id="description" rows={6} value={v.description} onChange={set("description")} placeholder="What are you looking to build or improve? Who will use it? Any timelines or existing systems we should know about?" className={`${inputCls(errors.description)} resize-y`} {...aria("description")} data-testid="contact-form-description" />
          </Field>
          <p className="mt-1.5 text-right font-mono text-[12px] text-slate-500">{v.description.length}/1500</p>
        </div>
        <fieldset className="sm:col-span-2">
          <legend className="text-[14px] font-semibold text-slate-900">Preferred contact method<span className="ml-0.5 text-red-600" aria-hidden="true">*</span></legend>
          <div className="mt-3 flex flex-wrap gap-3">
            {CONTACT_METHODS.map((m) => (
              <label key={m} className={`cursor-pointer rounded-full border px-5 py-2.5 text-[14px] font-semibold transition-colors focus-within:ring-4 focus-within:ring-blue-100 ${v.method === m ? "border-slate-950 bg-slate-950 text-white" : "border-slate-300 text-slate-800 hover:border-slate-500"}`}>
                <input type="radio" name="method" value={m} checked={v.method === m} onChange={set("method")} className="sr-only" data-testid={`contact-method-${m.toLowerCase().replace(" ", "-")}`} />{m}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="hidden" aria-hidden="true"><label htmlFor="website">Website</label><input id="website" tabIndex={-1} autoComplete="off" value={v.website} onChange={set("website")} /></div>
      </div>
      <button type="submit" className="group mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-950 px-7 py-4 text-[15px] font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-blue-700 active:scale-[0.98] sm:w-auto" data-testid="contact-form-submit">
        {v.method === "WhatsApp" ? "Continue in WhatsApp" : "Continue in email app"}
        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </button>
    </form>
  );
};
